import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_PAGE_SIZE = 48;

interface UseProgressiveRenderOptions {
  pageSize?: number;
  rootMargin?: string;
  minCount?: number;
}

interface UseProgressiveRenderResult<T> {
  visibleItems: T[];
  hasMore: boolean;
  sentinelRef: (node: HTMLElement | null) => void;
}

export function useProgressiveRender<T>(
  items: T[],
  { pageSize = DEFAULT_PAGE_SIZE, rootMargin = '400px', minCount }: UseProgressiveRenderOptions = {},
): UseProgressiveRenderResult<T> {
  const [visibleCount, setVisibleCount] = useState(pageSize);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // reset when a different list is passed in (identity check via first item id + length)
  const firstItemId = (items[0] as any)?.id;
  useEffect(() => {
    setVisibleCount(pageSize);
  }, [firstItemId, items.length, pageSize]);

  // callback ref: reconnects the observer whenever the sentinel element mounts/unmounts
  const sentinelRef = useCallback(
    (node: HTMLElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      if (!node) {
        return;
      }

      observerRef.current = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisibleCount((prev) => prev + pageSize);
          }
        },
        { rootMargin },
      );
      observerRef.current.observe(node);
    },
    [pageSize, rootMargin],
  );

  const effectiveCount = minCount !== undefined ? Math.max(visibleCount, minCount) : visibleCount;

  return {
    visibleItems: items.slice(0, effectiveCount),
    hasMore: effectiveCount < items.length,
    sentinelRef,
  };
}
